import { chain } from "@/config";
import { selectEtkByAddress } from "@store/eTokens/selectors";
import { selectPAByAddress } from "@store/premiumsAccounts/selectors";

interface LoadedState<T> {
  state: "LOADED";
  value: T;
}

interface LoadingState {
  state: "LOADING";
  value: undefined;
}

type AsyncState<T> = LoadedState<T> | LoadingState;

interface PremiumAccount {
  address: string;
  name: string;
}

interface EtkWithName {
  name: string;
  address: string;
}

interface RiskParams {
  moc: AsyncState<number>;
  jrCollRatio: AsyncState<number>;
  srCollRatio: AsyncState<number>;
  ensuroFee: AsyncState<number>;
  ensuroCoCFee: AsyncState<number>;
  jrRoc: AsyncState<number>;
  srRoc: AsyncState<number>;
}

interface VisibilityObject {
  custom_data?: {
    tags?: string[];
  };
}

export function getUtilizationRate(totalSupply: AsyncState<number>, scr: AsyncState<number>): string | undefined {
  if (totalSupply.state === "LOADED" && scr.state === "LOADED") {
    return totalSupply.value === 0 ? "0" : ((scr.value / totalSupply.value) * 100).toFixed(1);
  }
  return undefined;
}

export function calcOnLoad<T>(fn: (...args: any[]) => T, ...variables: AsyncState<any>[]): AsyncState<T> {
  if (variables.every((variable) => variable.state === "LOADED")) {
    const values = variables.map((variable) => variable.value);
    const result = fn(...values);
    return { state: "LOADED", value: result !== undefined ? result : (0 as T) };
  }
  return { state: "LOADING", value: undefined };
}

export function etherscanLink(address: string): string {
  return chain.scan + "address/" + address;
}

export function getAllPremiumsAccountsByEtk(
  etkPAs: string[] | null | undefined,
  allPAs: Record<string, string> | null | undefined
): PremiumAccount[] {
  let paInEtk: PremiumAccount[] = [];
  if (etkPAs && allPAs) {
    etkPAs.forEach((pa) => {
      const addr = new URL(pa).pathname.split("/")[3];
      paInEtk.push({
        address: addr,
        name: allPAs[addr],
      });
    });
  }
  return paInEtk;
}

export function checkVisibility(obj: any, mode: string): boolean {
  return !!obj?.custom_data && !(obj.custom_data.tags && obj.custom_data.tags.includes("hidden") && mode === "hide");
}

export function isJrOfSrFilter(etk: { sr_etks?: string[] }, srEtkFilter: string[], state: any, mode: string): boolean {
  return (
    etk.sr_etks?.some((srEtk) => {
      const addr = new URL(srEtk).pathname.split("/")[3];
      return srEtkFilter.includes(addr) && checkVisibility(selectEtkByAddress(state, addr), mode);
    }) || false
  );
}

export function isSrPool(etk: { sr_for_pas?: any[] }): boolean {
  return (etk.sr_for_pas?.length ?? 0) > 0;
}

export function getRiskParams(params: AsyncState<number[]> | null | undefined): RiskParams {
  const moc = params?.value
    ? { value: params.value[0], state: "LOADED" as const }
    : { state: "LOADING" as const, value: undefined };
  const jrCollRatio = params?.value
    ? { value: params.value[1], state: "LOADED" as const }
    : { state: "LOADING" as const, value: undefined };
  const srCollRatio = params?.value
    ? { value: params.value[2], state: "LOADED" as const }
    : { state: "LOADING" as const, value: undefined };
  const ensuroFee = params?.value
    ? { value: params.value[3], state: "LOADED" as const }
    : { state: "LOADING" as const, value: undefined };
  const ensuroCoCFee = params?.value
    ? { value: params.value[4], state: "LOADED" as const }
    : { state: "LOADING" as const, value: undefined };
  const jrRoc = params?.value
    ? { value: params.value[5], state: "LOADED" as const }
    : { state: "LOADING" as const, value: undefined };
  const srRoc = params?.value
    ? { value: params.value[6], state: "LOADED" as const }
    : { state: "LOADING" as const, value: undefined };
  return { moc, jrCollRatio, srCollRatio, ensuroFee, ensuroCoCFee, jrRoc, srRoc };
}

export function getEtkWithURL(reducer: any, url: string | null | undefined): AsyncState<EtkWithName> | {} {
  if (!url) return {};
  const addr = new URL(url).pathname.split("/")[3];
  if (!addr) return {};
  const etk = selectEtkByAddress(reducer, addr);
  return etk ? { state: "LOADED" as const, value: { name: etk.name, address: etk.address } } : {};
}

export function getPAWithURL(reducer: any, url: string | null | undefined): AsyncState<EtkWithName> | {} {
  if (!url) return {};
  const addr = new URL(url).pathname.split("/")[3];
  if (!addr) return {};
  const pa = selectPAByAddress(reducer, addr);
  return pa ? { state: "LOADED" as const, value: { name: pa.name, address: pa.address } } : {};
}
